import type { CardList } from './layout.model';

/**
 * Represents a page configuration within a view
 */
export interface PageConfig {
	/** Unique identifier for this page */
	pageId: string;

	/** Title displayed in navigation */
	title?: string;

	/** Optional description */
	description?: string;

	/** Reference to parent view */
	viewId: string;

	/** Order in navigation (1-based) */
	order?: number;

	/** Icon to display in navigation */
	icon?: string;

	/** When true, tab is hidden in view mode but shown in edit mode (e.g. drafts) */
	hidden?: boolean;

	/** Page-level filters */
	filters?: {
		[key: string]: PageFilter;
	};

	/** Required cards embedded in this page's single report layout. Empty pages must use `layout: []`. */
	layout: CardList[];

	/** Access control settings */
	access?: PageAccess;

	/** Timestamp when the page was created */
	dateCreated?: string;

	/** Number of revisions */
	revisions?: number;

	/** User ID who created the page */
	userId?: string;
}

/** GraphQL `PageItem` / `PageInput` use `isHidden`; client models use `hidden`. */
export function pageHiddenFromApi(
	raw: { hidden?: boolean; isHidden?: boolean } | null | undefined
): boolean | undefined {
	if (!raw) {
		return undefined;
	}
	if (typeof raw.isHidden === 'boolean') {
		return raw.isHidden;
	}
	if (typeof raw.hidden === 'boolean') {
		return raw.hidden;
	}
	return undefined;
}

/**
 * Filter configuration for a page
 */
export interface PageFilter {
	/** Type of filter */
	type: 'range' | 'select' | 'multiselect' | 'search';

	/** Whether to display this filter in the UI */
	show: boolean;

	/** Default value */
	default: any;

	/** Options for select filters */
	options?: Array<{ value: string; label: string }>;

	/** Whether to apply this filter to all layouts */
	applyToAllLayouts?: boolean;
}

/**
 * Access control for a page
 */
export interface PageAccess {
	/** Roles that can view this page */
	roles?: string[];

	/** Specific users that can view this page */
	users?: string[];
}

/**
 * Enforces the flattened page contract. Missing `layout` means the payload is
 * broken, not that the page is empty. Legacy `null` is normalized to `[]`.
 */
export function requirePageLayout(page: {
	layout: unknown;
	pageId?: string | null;
}): CardList[] {
	if (page.layout === null) {
		return [];
	}

	if (!Array.isArray(page.layout)) {
		const pageLabel = page.pageId ? ` "${page.pageId}"` : '';
		throw new Error(
			`Page${pageLabel} is missing required page.layout array. Empty pages must provide layout: [].`
		);
	}

	return page.layout;
}
